import type { ReactNode } from "react";
import { UserPlus, X } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { relationshipService } from "@project/services/admin/relationship.service";
import {
  REFERENCE_SOURCE_OPTIONS,
  newFamilyMemberRow,
  type VisitorFamilyMemberFormRow,
} from "@project/types/visitor";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

export type VisitorFormFieldsVariant = "full" | "inquiry";

type VisitorFormFieldsProps = {
  editing: Record<string, unknown> | null;
  setEditing: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >;
  /** `inquiry` shows Visit Details, Reason for Visit, and Reference only. */
  variant?: VisitorFormFieldsVariant;
};

function patch(
  editing: Record<string, unknown> | null,
  setEditing: VisitorFormFieldsProps["setEditing"],
  updates: Record<string, unknown>
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

function getFamilyMembers(
  editing: Record<string, unknown> | null
): VisitorFamilyMemberFormRow[] {
  return Array.isArray(editing?.family_members)
    ? (editing.family_members as VisitorFamilyMemberFormRow[])
    : [];
}

async function searchRelationships(query: string) {
  const result = await relationshipService.listRelationshipsPage({
    page: 1,
    page_size: 50,
    search: query || undefined,
  });
  return result.data
    .filter(r => r.is_active)
    .map(r => ({
      value: String(r.id),
      label: r.relationship_name,
      secondary: r.relationship_code,
    }));
}

function seed(id: string, name: string) {
  return id && name ? { value: id, label: name } : undefined;
}

export function VisitorFormFields({
  editing,
  setEditing,
  variant = "full",
}: VisitorFormFieldsProps) {
  const isInquiry = variant === "inquiry";
  const family = getFamilyMembers(editing);
  const hasVisitedBefore = Boolean(editing?.has_visited_before);

  const setField = (key: string, value: unknown) => {
    patch(editing, setEditing, { [key]: value });
  };

  const updateFamily = (
    rowId: string,
    updates: Partial<VisitorFamilyMemberFormRow>
  ) => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: getFamilyMembers(prev).map(row =>
        row.rowId === rowId ? { ...row, ...updates } : row
      ),
    }));
  };

  const addFamilyMember = () => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: [
        ...getFamilyMembers(prev),
        newFamilyMemberRow(getFamilyMembers(prev).length + 1),
      ],
    }));
  };

  const removeFamilyMember = (rowId: string) => {
    setEditing(prev => ({
      ...(prev ?? {}),
      family_members: getFamilyMembers(prev)
        .filter(row => row.rowId !== rowId)
        .map((row, index) => ({ ...row, sr_no: index + 1 })),
    }));
  };

  return (
    <div className="space-y-6">
      <Section title="Visit Details">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Visit Date"
            type="date"
            value={String(editing?.date ?? "")}
            onChange={v => setField("date", v)}
            required
          />
          <Field
            label="Visitor Name"
            value={String(editing?.name ?? "")}
            onChange={v => setField("name", v)}
            required
          />
          <Field
            label="Age"
            type="number"
            value={String(editing?.age ?? "")}
            onChange={v => setField("age", v === "" ? undefined : Number(v))}
          />
          <Field
            label="Occupation"
            value={String(editing?.occupation ?? "")}
            onChange={v => setField("occupation", v)}
          />
          <div>
            <Label>Mobile Number</Label>
            <MobileNumberField
              value={String(editing?.mobile_number ?? "")}
              country={
                (editing?.mobile_country as PhoneCountryCode | undefined) ??
                DEFAULT_PHONE_COUNTRY
              }
              onCountryChange={code => setField("mobile_country", code)}
              onChange={v => setField("mobile_number", v)}
              inputClassName="rounded-xl border-input bg-background"
            />
          </div>
          <Field
            label="Residence Phone"
            type="tel"
            value={String(editing?.residence_phone ?? "")}
            onChange={v => setField("residence_phone", v)}
          />
          <Field
            label="Email"
            type="email"
            value={String(editing?.email ?? "")}
            onChange={v => setField("email", v)}
          />
        </div>
        <TextField
          label="Address"
          value={String(editing?.address ?? "")}
          onChange={v => setField("address", v)}
        />
        <TextField
          label="Business Address"
          value={String(editing?.business_address ?? "")}
          onChange={v => setField("business_address", v)}
        />
      </Section>

      <Section title="Reason for Visit">
        <TextField
          label="Reason for Visit"
          value={String(editing?.reason_for_visit ?? "")}
          onChange={v => setField("reason_for_visit", v)}
        />
        <div className="bg-muted/30 flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <Label>Visited Before</Label>
            <p className="text-xs text-muted-foreground">
              Has this visitor been here previously?
            </p>
          </div>
          <Switch
            checked={hasVisitedBefore}
            onCheckedChange={checked => {
              patch(editing, setEditing, {
                has_visited_before: checked,
                previous_visit_details: checked
                  ? String(editing?.previous_visit_details ?? "")
                  : "",
              });
            }}
          />
        </div>
        {hasVisitedBefore ? (
          <TextField
            label="Previous Visit Details"
            value={String(editing?.previous_visit_details ?? "")}
            onChange={v => setField("previous_visit_details", v)}
          />
        ) : null}
      </Section>

      <Section title="Reference">
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectLike label="Reference Source">
            <Select
              value={String(editing?.reference_source ?? "") || undefined}
              onValueChange={v => setField("reference_source", v)}
            >
              <SelectTrigger className="rounded-xl border-input bg-background">
                <SelectValue placeholder="Select source..." />
              </SelectTrigger>
              <SelectContent>
                {REFERENCE_SOURCE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SelectLike>
          <Field
            label="Referrer Name"
            value={String(editing?.referrer_name ?? "")}
            onChange={v => setField("referrer_name", v)}
          />
          <div>
            <Label>Referrer Mobile</Label>
            <MobileNumberField
              value={String(editing?.referrer_mobile ?? "")}
              country={
                (editing?.referrer_mobile_country as
                  | PhoneCountryCode
                  | undefined) ?? DEFAULT_PHONE_COUNTRY
              }
              onCountryChange={code =>
                setField("referrer_mobile_country", code)
              }
              onChange={v => setField("referrer_mobile", v)}
              inputClassName="rounded-xl border-input bg-background"
            />
          </div>
        </div>
      </Section>

      {!isInquiry ? (
        <>
          <Section title="Family Summary">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label="Total Family Members"
                type="number"
                value={String(editing?.total_family_members ?? "")}
                onChange={v =>
                  setField(
                    "total_family_members",
                    v === "" ? undefined : Number(v)
                  )
                }
              />
              <Field
                label="Male Count"
                type="number"
                value={String(editing?.male_count ?? "")}
                onChange={v =>
                  setField("male_count", v === "" ? undefined : Number(v))
                }
              />
              <Field
                label="Female Count"
                type="number"
                value={String(editing?.female_count ?? "")}
                onChange={v =>
                  setField("female_count", v === "" ? undefined : Number(v))
                }
              />
            </div>
          </Section>

          <Section title={`Family Members (${family.length})`}>
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addFamilyMember}
                className="gap-1 rounded-xl"
              >
                <UserPlus className="h-3 w-3" /> Add Family Member
              </Button>
            </div>
            {family.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No family members added yet.
              </p>
            ) : (
              family.map(row => (
                <div
                  key={row.rowId}
                  className="bg-muted/20 relative space-y-3 rounded-xl border border-border p-4"
                >
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-1 top-1 h-7 w-7 text-destructive"
                    onClick={() => removeFamilyMember(row.rowId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Sr. No. {row.sr_no}</Label>
                      <Input
                        readOnly
                        value={String(row.sr_no)}
                        className="bg-muted/40 rounded-xl border-input"
                      />
                    </div>
                    <Field
                      label="Name"
                      value={row.name}
                      onChange={v => updateFamily(row.rowId, { name: v })}
                      required
                    />
                    <SelectLike label="Relationship" required>
                      <SearchableSelect
                        value={row.relationship}
                        onValueChange={v =>
                          updateFamily(row.rowId, { relationship: v })
                        }
                        onSearch={searchRelationships}
                        seedOption={seed(
                          row.relationship,
                          row.relationshipName
                        )}
                        placeholder="Select relationship..."
                        searchPlaceholder="Search relationship..."
                        emptyText="No relationship found."
                      />
                    </SelectLike>
                    <Field
                      label="Date of Birth"
                      type="date"
                      value={row.date_of_birth}
                      onChange={v =>
                        updateFamily(row.rowId, { date_of_birth: v })
                      }
                    />
                    <Field
                      label="Education"
                      value={row.education}
                      onChange={v => updateFamily(row.rowId, { education: v })}
                    />
                  </div>
                </div>
              ))
            )}
          </Section>

          <Section title="Feedback">
            <TextField
              label="Feedback"
              value={String(editing?.feedback ?? "")}
              onChange={v => setField("feedback", v)}
            />
          </Section>
        </>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
      <h3 className="text-sm font-bold uppercase tracking-wider text-primary">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <Label data-required={required ? "true" : undefined}>{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="rounded-xl border-input bg-background"
        aria-required={required ? true : undefined}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        rows={2}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="rounded-xl border-input bg-background"
      />
    </div>
  );
}

function SelectLike({
  label,
  children,
  required,
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div>
      <Label data-required={required ? "true" : undefined}>{label}</Label>
      {children}
    </div>
  );
}
